<template>
    <div class="relative">
        <!-- Show Issues by Required Action -->
        <div v-show="issues && issues.length > 0" class="mt-4 z-20">
            <div class="flex flex-wrap items-center justify-between">
                <div class="flex items-center select-none gap-x-2 h-6">
                    <checkbox v-if="issues.length > 1" @input="selectAllIssues" v-model="allIssuesSelected"></checkbox>
                    <span class="font-medium text-md">Issues</span>
                </div>

                <bulk-update-issues-dropdown v-if="form.selected_issues && form.selected_issues.length > 0"
                                             :action-types="issueActionTypes" @action-selected="bulkUpdateIssues">
                </bulk-update-issues-dropdown>
            </div>

            <!-- Errors and warnings-->
            <div class="text-red-500 text-xs break-normal mt-1" v-show="form.errors.has('selected_issues')">
                {{ form.errors.get("selected_issues") }}
            </div>
            <div class="text-yellow-600 text-xs my-2"
                 v-if="statusObject && statusObject.status === 'quote approved' && !anyQuotedIssuesSelected">
                You are approving a quote but do not have any Quote or Proceed As Quoted issues selected
            </div>

            <!-- List of issues that are selectable -->
            <ul id="selected-issues" style="max-height: 27.5rem"
                class="mt-2 space-y-3 mt-1 overscroll-none overflow-y-auto scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100"
                :class="{'pr-4 md:pr-2' : checkSelectionListOverflow}">
                <li v-for="issue in issues" :key="issue.id"
                    class="flex items-center h-auto w-full bg-indigo-50 p-2 rounded-md space-x-2">

                    <div class="flex flex-col sm:flex-row items-center justify-between gap-y-2 sm:gap-y-0 gap-x-0 sm:gap-x-2">
                        <!-- todo replace with a new checkbox component that can handle array v-models-->
                        <input type="checkbox"
                               class="focus:ring-indigo-600 h-4 w-4 text-indigo-600 border-gray-400 rounded cursor-pointer hover:border-indigo-600 transition duration-300"
                               :value="issue" :key="issue.id" v-model="form.selected_issues">

                        <!-- Show Issue Photo Preview -->
                        <button @click="toggleIssuePhoto(issue)" class="outline-none">
                            <icon-photograph class="h-5 w-5" :class="issue.photo ? 'text-indigo-600' :'text-indigo-300 cursor-default'"></icon-photograph>
                        </button>
                    </div>

                    <!-- Issue information -->
                    <div class="flex flex-col w-full">
                        <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                            <div class="flex flex-col">
                                <div class="flex items-center">
                                    <span v-if="issue.location" class="font-bold mr-1">{{ issue.location }}</span>
                                    <span class="font-normal">{{ issue.item }}</span>
                                </div>

                                <div v-if="issue.description">
                                    <span class="pr-2 text-xs">{{ issue.description }}</span>
                                </div>
                            </div>

                            <!-- Allow issues' required_action to change -->
                            <simple-dropdown :label="issue.required_action" @input="setRequiredAction($event, issue)"
                                             button-class="text-indigo-600 items-center hover:text-indigo-800 font-medium pl-0 pr-0 sm:pl-3 sm:pr-2">
                                <template slot="buttonOptions">
                                    <simple-dropdown-option v-for="requiredAction in issueActionTypes"
                                                            :key="Math.random() * 9999999"
                                                            :value="requiredAction"
                                                            :button-class="requiredAction === issue.required_action ? 'text-indigo-600' : ''">
                                        {{ requiredAction }}
                                    </simple-dropdown-option>
                                </template>
                            </simple-dropdown>
                        </div>
                    </div>
                </li>
            </ul>
        </div>

        <!-- Empty View -->
        <div v-if="issues && issues.length === 0" class="flex justify-center items-center mt-20">
            <span class=" font-medium text-lg">This ticket does not have any issues</span>

            <div class="text-red-500 text-xs break-normal mt-1" v-show="form.errors.has('selected_issues')">
                {{ form.errors.get("selected_issues") }}
            </div>
        </div>


        <lightbox :images="lightboxImageUrls" :show-lightbox="showLightbox" @close="showLightbox = false" :width="400" :height="400"></lightbox>
    </div>
</template>

<script>
export default {
    name: "TaskServiceProviderModalSelectIssues",

    props: {
        form: {
            required: true,
            type: Object,
        },
        ticketId: {
            required: false,
            type: Number,
            Description: "Used to fetch issues from the ticket",
            default: null,
        },
        statusObject: {
            required: false,
            type: Object,
            default: null,
            description: "Used when Quote Approved/Rejected button is used to pre-populate modal and pre-select action_required"
        },
    },

    data () {
        return {
            issues: [],
            //constants on Issue
            issueActionTypes: [
                "No Action",
                "Quote",
                "Proceed As Quoted",
                "Observe & Comment",
                "Attend On Call Out",
                "Completed"
            ],
            allIssuesSelected: false,
            showLightbox: false,
            lightboxImageUrls: [],
        };
    },

    computed: {
        anyQuotedIssuesSelected () {
            return this.form && this.form.selected_issues && this.form.selected_issues.length > 0 ? (this.form.selected_issues.filter(issue => issue.required_action === "Quote" || issue.required_action === "Proceed As Quoted")).length > 0 : false;
        },
    },

    mounted () {
        if (this.form && this.form.selected_issues && this.form.selected_issues.length === 0) {
            this.fetchIssues();
        }

        let self = this;
        Bus.$on("issues-updated", function () {
            self.fetchIssues();
        });
    },

    watch: {
        "form.selected_issues": function (list) {
            if (this.form.errors.has("selected_issues")) {
                this.form.errors.forget("selected_issues");
            }

            this.allIssuesSelected = !!list && list.length !== 0 && list.length === this.issues.length;
        },

        statusObject () {
            //set default action_type for each selected issue
            if (this.statusObject && this.statusObject.serviceProvider !== null) {
                if (this.statusObject.status === "quote approved") {
                    //automatically select quote and proceed as quoted issues
                    this.form.selected_issues = this.issues.filter(issue => issue.required_action === "Quote" || issue.required_action === "Proceed As Quoted");
                }
            } else {
                this.form.selected_issues.splice(0);
            }
        },
    },

    methods: {
        fetchIssues () {
            this.loading = true;
            this.form.selected_issues.splice(0);
            axios.get("/tickets/" + this.ticketId + "/issues/all")
            .then(response => {
                this.issues = response.data;
                if (this.issues && this.issues.length > 0) {
                    this.sortIssues();
                    this.$emit("toggle-next-button", false);
                } else {
                    this.$emit("toggle-next-button", true);
                }

                this.loading = false;
            }).catch(error => {
                this.loading = false;
                window.toastr.error("Error Retrieving Ticket Issues! Error: " + error.message);
            });
        },

        toggleIssuePhoto (issue) {
            this.lightboxImageUrls = [];

            if (!issue.photo) {
                this.showLightbox = false;
                return;
            }

            axios.get(`/tickets/issues/${issue.id}/photos`)
            .then(response => {
                this.showLightbox = !this.showLightbox;
                let media = response.data;
                this.lightboxImageUrls = media.map(media => media.url);
            });
        },

        sortIssues () {
            if (this.issues && this.issues.length > 0) {
                this.issues = _.sortBy(this.issues, "required_action");
            }
        },

        setRequiredAction (requiredAction, issue) {
            if (this.form.selected_issues && this.form.selected_issues.length > 0) {
                let selectedIssue = this.form.selected_issues.find(i => i.id === issue.id);
                if (selectedIssue) {
                    selectedIssue.required_action = requiredAction;
                }
            }
            issue.required_action = requiredAction;
        },

        selectAllIssues () {
            if (this.issues.length !== this.form.selected_issues.length) {
                // Add all which have not been selected
                this.issues.forEach((issue) => {
                    if (this.form.selected_issues.findIndex(selectedIssue => selectedIssue.id === issue.id) === -1) {
                        // use this trick to create a temp object for deep cloning and not just cloning with reference
                        let temp = JSON.parse(JSON.stringify(issue));
                        this.form.selected_issues.push(temp);
                    }
                });
            } else {
                this.deselectAllIssues();
            }
        },

        deselectAllIssues () {
            this.form.selected_issues.splice(0);
        },

        bulkUpdateIssues (requiredAction) {
            this.form.selected_issues.forEach(selectedIssue => {
                this.issues.forEach(issue => {
                    if (issue.id === selectedIssue.id) {
                        issue.required_action = requiredAction;
                        selectedIssue.required_action = requiredAction;
                    }
                });
            });
        },

        checkSelectionListOverflow () {
            let content = document.getElementById("selected-issues");
            // Give the dom .05 seconds to update the content's scrollHeight
            if (content) {
                return content.scrollHeight > content.clientHeight;
            }
            return false;
        },
    },
};
</script>